import { useState, useRef, useEffect, useCallback } from 'react'
import backgroundImg from './assets/background.png'
import { IntroScreen } from './components/IntroScreen'
import { InactivityLockScreen } from './components/InactivityLockScreen'
import { UnauthorizedOverlay } from './components/UnauthorizedOverlay'
import { useConversation } from './hooks/useConversation'
import { useNavigation } from './hooks/useNavigation'
import { useInactivityTimeout } from './hooks/useInactivityTimeout'
import { useBiometricAuth } from './hooks/useBiometricAuth'
import { useNetworkSync, useOnline } from './hooks/useNetwork'
import { setOn403, pwaApi } from './api'
import { Screen1 } from './screens/Screen1'
import { Screen2 } from './screens/Screen2'
import { Screen3 } from './screens/Screen3'
import { IncidentFormScreen } from './screens/IncidentFormScreen'
import { IncidentSummaryScreen } from './screens/IncidentSummaryScreen'
import { registerServiceWorker } from './pwaUpdate'

type Screen = 1 | 2 | 3
type TransitionType = 'slide-left' | 'slide-right' | 'crossfade' | 'scale-fade' | 'scale-unfade'
type AnimPhase = 'idle' | 'start' | 'end'

const SPRING = 'cubic-bezier(0.25, 1, 0.2, 1)'
const SLIDE_MS = 700
const FADE_MS = 550

const REFERENCE_HEIGHT = 812
const REFERENCE_WIDTH  = 390

// E1: Stałe wymiary w px (niezależne od wysokości ekranu!)
const MIC_RADIUS_PX   = 104.5       // Promień DuzyMic
const SEARCH_H_PX     = 35          // Zwinięty SearchBar
const TITLE_H_PX      = 39
const ACTIONBAR_H_PX  = 75
const DECISION_H_PX   = 450
const NOTEBAR_H_PX    = 156

// E1: Elastyczne odstępy - skalują się z ekranem, min 20px max 50px
const TOKENBAR_BOTTOM_PX = 68         // TokenBar od dołu (stałe)
const MIN_GAP_PX = 20                 // Minimalny odstęp między elementami
const MAX_GAP_PX = 50                 // Maksymalny odstęp

function getScreenStyle(
  screenId: Screen,
  activeScreen: Screen,
  phase: AnimPhase,
  transition: TransitionType,
  leavingScreen: Screen,
): React.CSSProperties {
  const isActive  = screenId === activeScreen
  const isLeaving = screenId === leavingScreen && !isActive

  const dur = (transition === 'crossfade' || transition === 'scale-fade' || transition === 'scale-unfade')
    ? FADE_MS : SLIDE_MS
  const tProp = `opacity ${dur}ms ${SPRING}, transform ${dur}ms ${SPRING}`

  const center = 'translateX(0px) translateY(-50%)'
  const base: React.CSSProperties = {
    position: 'absolute', width: '100%', height: '100%', top: '50%', left: 0,
  }

  if (phase === 'idle') {
    if (isActive) return { ...base, zIndex: 1, opacity: 1, transform: center, pointerEvents: 'auto', transition: 'none' }
    return { ...base, zIndex: 0, opacity: 0, transform: center, pointerEvents: 'none', transition: 'none' }
  }

  const enterTransforms: Record<TransitionType, string> = {
    'slide-left':   'translateX(120px) translateY(-50%)',
    'slide-right':  'translateX(-120px) translateY(-50%)',
    'crossfade':    'translateX(0px) translateY(-50%)',
    'scale-fade':   'translateX(0px) translateY(-50%) scale(1.1)',
    'scale-unfade': 'translateX(0px) translateY(-50%) scale(0.9)',
  }
  const leaveTransforms: Record<TransitionType, string> = {
    'slide-left':   'translateX(-120px) translateY(-50%)',
    'slide-right':  'translateX(120px) translateY(-50%)',
    'crossfade':    'translateX(0px) translateY(-50%)',
    'scale-fade':   'translateX(0px) translateY(-50%) scale(0.9)',
    'scale-unfade': 'translateX(0px) translateY(-50%) scale(1.1)',
  }

  if (phase === 'start') {
    if (isActive)  return { ...base, zIndex: 2, opacity: 0, transform: enterTransforms[transition], pointerEvents: 'none', transition: 'none' }
    if (isLeaving) return { ...base, zIndex: 1, opacity: 1, transform: center, pointerEvents: 'none', transition: 'none' }
    return { ...base, zIndex: 0, opacity: 0, transform: center, pointerEvents: 'none', transition: 'none' }
  }

  if (phase === 'end') {
    if (isActive)  return { ...base, zIndex: 2, opacity: 1, transform: center, pointerEvents: 'auto', transition: tProp }
    if (isLeaving) return { ...base, zIndex: 1, opacity: 0, transform: leaveTransforms[transition], pointerEvents: 'none', transition: tProp }
    return { ...base, zIndex: 0, opacity: 0, transform: center, pointerEvents: 'none', transition: 'none' }
  }

  return { ...base, zIndex: 0, opacity: 0, transform: center, pointerEvents: 'none' }
}

function App() {
  useNetworkSync()
  const isOnline = useOnline()

  const [screen, setScreen]               = useState<Screen>(1)
  const [leavingScreen, setLeavingScreen]  = useState<Screen>(1)
  const [phase, setPhase]                  = useState<AnimPhase>('idle')
  const [transition, setTransition]        = useState<TransitionType>('slide-left')
  const animTimer  = useRef<ReturnType<typeof setTimeout> | null>(null)
  const rafRef     = useRef<number>(0)
  const phaseRef   = useRef<AnimPhase>('idle')
  const screenRef  = useRef<Screen>(1)

  const [fontSize, setFontSize]        = useState(15)
  const [unauthorized, setUnauthorized] = useState<{ active: boolean; reason?: string }>({ active: false })

  // Podłącz globalny callback 403 z api.ts
  useEffect(() => {
    setOn403((reason) => setUnauthorized({ active: true, reason }))
  }, [])

  // Wysyłka zapisanych offline raportów z incydentu po powrocie online
  useEffect(() => {
    const flushQueue = async () => {
      const raw = localStorage.getItem('incidentQueue')
      const queue = raw ? JSON.parse(raw) : []
      if (!queue.length || !isOnline) return
      const remaining: any[] = []
      for (const item of queue) {
        try { await pwaApi.sendIncidentReport(item) }
        catch { remaining.push(item) }
      }
      localStorage.setItem('incidentQueue', JSON.stringify(remaining))
    }
    window.addEventListener('online', flushQueue)
    flushQueue()
    return () => window.removeEventListener('online', flushQueue)
  }, [isOnline])
  const [viewportHeight, setViewportHeight] = useState(typeof window !== 'undefined' ? window.innerHeight : REFERENCE_HEIGHT)
  const [viewportWidth, setViewportWidth]   = useState(typeof window !== 'undefined' ? window.innerWidth  : REFERENCE_WIDTH)
  const [micScale, setMicScale]             = useState(1)
  // E1: Odstępy pionowe - kolejność od dołu: TokenBar -> DuzyMic -> SearchBar -> Lista -> Tytuł
  const [screen1Pos, setScreen1Pos] = useState({
    titleTop: 6.1,                     // Tytuł na górze (podniesiony o ~40px)
    employeeListTop: 18.0,             // Górna krawędź listy
    employeeListTopWorking: 5.0,       // Górna krawędź listy gdy search rozwinięty (może być ujemna!)
    searchBarTop: 43.0,                // Górna krawędź SearchBar (zwinięty)
    searchBarBottomExpand: 280,        // Wysokość rozwinięcia w GÓRĘ (px)
    mlotButtonTop: 56.5,               // Górna krawędź DuzyMic
    tokenBarBottom: 8.4,               // TokenBar bottom
  })
  const [screen2Pos, setScreen2Pos] = useState({
    titleTop: 6.1, convTop: 12.1, actionBarBottom: 10.5, convHeightPct: 57.8,
  })
  const [screen3Pos, setScreen3Pos] = useState({
    titleTop: 6.1, decisionTop: 12.7, decisionHeightPct: 75.0,
    noteBarTopDOM: 50.5, noteBarScale: 1.0, actionBarBottom: 10.5,
  })

  const [showIntro, setShowIntro] = useState(true)
  const [showLogo, setShowLogo]   = useState(false)
  const [e1Visible, setE1Visible] = useState(false)
  const [splashReady, setSplashReady] = useState(false) // splash zatrzymał się w połowie
  const splashUnlockRef = useRef<(() => void) | null>(null) // callback do dokończenia splasha

  const conv = useConversation()
  const { authState, authReason, lock, unlock, unlockWithToken, setOnLockWipe } = useBiometricAuth()
  useInactivityTimeout(lock, authState === 'authenticated')
  const [resetSignal, setResetSignal] = useState(0)
  const bumpReset = useCallback(() => setResetSignal(s => s + 1), [])

  // ── App Switcher Guard (maskowanie miniaturki + auto-lock po powrocie z tła) ──
  const [appHidden, setAppHidden] = useState(false)
  const wasHiddenRef = useRef(false)

  useEffect(() => {
    const handleVisibility = () => {
      if (document.visibilityState === 'hidden') {
        setAppHidden(true)
        wasHiddenRef.current = true
      } else {
        setAppHidden(false)
        // Jeśli użytkownik faktycznie wrócił z tła (a nie tylko otworzył app),
        // wymuś natychmiastową blokadę biometryczną.
        if (wasHiddenRef.current && authState === 'authenticated') {
          lock()
        }
        wasHiddenRef.current = false
      }
    }
    const handleBlur = () => setAppHidden(true)
    const handleFocus = () => {
      setAppHidden(false)
      // Dla desktopów/kart: utrata focusu i powrót również wymuszają lock.
      if (wasHiddenRef.current && authState === 'authenticated') {
        lock()
      }
      wasHiddenRef.current = false
    }

    document.addEventListener('visibilitychange', handleVisibility)
    window.addEventListener('blur', handleBlur)
    window.addEventListener('focus', handleFocus)
    return () => {
      document.removeEventListener('visibilitychange', handleVisibility)
      window.removeEventListener('blur', handleBlur)
      window.removeEventListener('focus', handleFocus)
    }
  }, [authState, lock])

  // ── RAM wipe callback ──────────────────────────────────────────────
  useEffect(() => {
    setOnLockWipe(() => {
      conv.resetConversation()
      conv.setApiEmployees(null)
      conv.setConversationItems(null)
    })
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [setOnLockWipe])

  const dbg = (msg: string) => console.log('[dbg]', msg)

  // ── Uruchom splash logo od razu przy mount ────────────────────────────────
  useEffect(() => {
    setShowLogo(true)
  }, [])

  // ── Gdy authState rozstrzygnięty — dokończ splash lub pokaż lock ──────────
  useEffect(() => {
    if (authState === 'loading') return
    // Splash zatrzymał się w połowie — teraz możemy kontynuować
    if (authState === 'authenticated') {
      // Odblokuj splash do dokończenia animacji
      if (splashUnlockRef.current) {
        splashUnlockRef.current()
        splashUnlockRef.current = null
      } else {
        // Splash już zakończony (np. cicha auth trwała dłużej) — po prostu pokaż E1
        setShowIntro(false)
        setE1Visible(true)
      }
    } else {
      // locked / unauthorized — zatrzymaj splash, pokaż ekran auth na wierzchu
      setSplashReady(true)
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authState])

  // Zarejestruj service worker — umożliwia cache'owanie PWA, działanie offline
  // i auto-aktualizację (patrz pwaUpdate.ts — sprawdzanie dodatkowo przy powrocie
  // połączenia w useNetworkSync, z bezpiecznikiem przeciw przerwaniu dyktowania).
  useEffect(() => {
    registerServiceWorker()
  }, [])

  useEffect(() => {
    conv.loadConversationItems()
  }, [])

  // ── Viewport resize ──────────────────────────────────────────────────────
  useEffect(() => {
    const handleResize = () => { setViewportHeight(window.innerHeight); setViewportWidth(window.innerWidth) }
    window.addEventListener('resize', handleResize)
    return () => window.removeEventListener('resize', handleResize)
  }, [])

  useEffect(() => {
    const h = viewportHeight
    const pct = (px: number) => (px / h) * 100
    const r = Math.min(h / REFERENCE_HEIGHT, 1)
    const minOuter = h * 0.05; const minInner = 8

    // ── E1: układ od dołu do góry ──────────────────────────────────────────
    // Krok 1: stałe anchory
    const tokenBarBottomPx = TOKENBAR_BOTTOM_PX          // TokenBar: 68px od dołu
    const titleTopPx       = h * 0.095

    // Krok 2: DuzyMic i SearchBar od dołu
    const gapPx            = Math.min(Math.max(MIN_GAP_PX * r, MIN_GAP_PX), MAX_GAP_PX)
    const micTopPx         = h - tokenBarBottomPx - gapPx - (MIC_RADIUS_PX * 2)
    const searchBarBottomPx = micTopPx - gapPx
    const searchBarTopPx   = searchBarBottomPx - SEARCH_H_PX

    // Krok 3: gap między dolną krawędzią listy a górną krawędzią SearchBar — stały dla ekranu
    const listSearchGapPx  = Math.round(Math.min(Math.max(h * 0.012, 6), 14))
    // Lista ma hardcoded h=173px w EmployeeList — pozycjonujemy ją od dołu (tuż nad SearchBar)
    const LIST_CARD_H      = 173
    const listBottomPx     = searchBarTopPx - listSearchGapPx
    const listTopPx        = listBottomPx - LIST_CARD_H

    // SearchBar rozwinięty: outer wrapper (anchor top) NIE rusza się
    // inner div rośnie z bottom=0 w górę do expandHeightPx=180px
    // wizualna górna krawędź SearchBar unosi się o: (180 - SEARCH_H_PX) = 145px powyżej anchora
    const expandHeightPx     = 180   // całkowita wysokość rozwinięta (przekazywana do SearchBar)
    const visualRisePx       = expandHeightPx - SEARCH_H_PX  // = 145px — o ile górna krawędź wizualna się uniosła

    // Lista przesuwa się w górę o DOKŁADNIE visualRisePx (gap od wizualnej górnej krawędzi SearchBar NIEZMIENIONY)
    const listTopWorkingPx   = listTopPx - visualRisePx

    // Skala mikrofonu
    setMicScale(Math.min(1, r * 0.95))

    setScreen1Pos({
      titleTop: pct(titleTopPx),
      employeeListTop: pct(listTopPx),
      employeeListTopWorking: pct(listTopWorkingPx),
      searchBarTop: pct(searchBarTopPx),
      searchBarBottomExpand: expandHeightPx,
      mlotButtonTop: pct(micTopPx),
      tokenBarBottom: pct(tokenBarBottomPx),
    })

    // Layout 2
    const ab2 = Math.max(85.3 * r, minOuter)
    const abTop2 = h - ab2 - ACTIONBAR_H_PX
    const convTop2 = titleTopPx + TITLE_H_PX + Math.max(15 * r, minInner)
    setScreen2Pos({
      titleTop: pct(titleTopPx), convTop: pct(convTop2),
      actionBarBottom: pct(ab2),
      convHeightPct: pct(Math.max(abTop2 - Math.max(24 * r, minInner) - convTop2, 80)),
    })

    // Layout 3
    const ab3 = Math.max(85.3 * r, minOuter)
    const abTop3 = h - ab3 - ACTIONBAR_H_PX
    const decTop3 = titleTopPx + TITLE_H_PX + Math.max(15 * r, minInner)
    const slotH3 = abTop3 - Math.max(16 * r, minInner) - decTop3
    const gap3 = Math.max(20 * r, minInner)
    const totalVis = DECISION_H_PX * r + gap3 + NOTEBAR_H_PX * r
    const s3 = Math.max(slotH3 >= totalVis ? r : r * (slotH3 / totalVis), 0.4)
    const decH3 = h * 0.60  // 60% wysokości ekranu - responsywnie
    const noteH3 = NOTEBAR_H_PX * s3
    const spare3 = Math.max(slotH3 - decH3 - Math.max(20 * s3, minInner) - noteH3, 0)
    const decTop3final = decTop3 + spare3 / 2
    const noteTop3 = decTop3final + decH3 + Math.max(20 * s3, minInner)
    setScreen3Pos({
      titleTop: pct(titleTopPx), decisionTop: pct(decTop3final),
      decisionHeightPct: pct(decH3),
      noteBarTopDOM: pct(noteTop3 - (NOTEBAR_H_PX - noteH3) / 2),
      noteBarScale: s3, actionBarBottom: pct(ab3),
    })
  }, [viewportHeight])

  // ── Auth startup ─────────────────────────────────────────────────────────
  // Token handling przeniesione do useBiometricAuth — tu tylko ładujemy dane po autentykacji
  useEffect(() => {
    if (authState === 'authenticated') {
      conv.loadEmployees()
      conv.loadConversationItems()
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authState])

  useEffect(() => {

    const showLogoNow = () => setShowLogo(true)

    if (localStorage.getItem('mic_granted')) {
      showLogoNow()
    } else {
      const micTimeout = setTimeout(showLogoNow, 8000)
      navigator.mediaDevices?.getUserMedia({ audio: true })
        .then(stream => {
          stream.getTracks().forEach(t => t.stop())
          localStorage.setItem('mic_granted', '1')
          clearTimeout(micTimeout)
          showLogoNow()
        })
        .catch(() => {
          clearTimeout(micTimeout)
          showLogoNow()
        })
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => () => {
    if (animTimer.current) clearTimeout(animTimer.current)
    cancelAnimationFrame(rafRef.current)
  }, [])

  // ── Nawigacja z animacją ─────────────────────────────────────────────────
  // nav musi być zadeklarowane po navigateTo — inicjalizowane niżej
  const transitionMap: Record<string, TransitionType> = {
    '1→2': 'slide-left', '2→1': 'slide-right',
    '2→3': 'crossfade',  '3→2': 'scale-unfade',
    '3→1': 'scale-fade', '1→3': 'crossfade',
  }

  const navigateTo = (next: Screen) => {
    if (phaseRef.current !== 'idle' || next === screenRef.current) return
    const key   = `${screenRef.current}→${next}`
    const tType = transitionMap[key] ?? 'crossfade'
    const dur   = (tType === 'crossfade' || tType === 'scale-fade' || tType === 'scale-unfade') ? FADE_MS : SLIDE_MS

    setLeavingScreen(screenRef.current)
    setTransition(tType)
    screenRef.current = next
    setScreen(next)
    phaseRef.current = 'start'
    setPhase('start')

    cancelAnimationFrame(rafRef.current)
    rafRef.current = requestAnimationFrame(() => {
      rafRef.current = requestAnimationFrame(() => {
        phaseRef.current = 'end'
        setPhase('end')
        if (animTimer.current) clearTimeout(animTimer.current)
        animTimer.current = setTimeout(() => {
          phaseRef.current = 'idle'
          setPhase('idle')
        }, dur + 20)
      })
    })
  }

  // nav zadeklarowane po navigateTo — wymóg TypeScript (no use before declaration)
  // eslint-disable-next-line react-hooks/rules-of-hooks
  const nav = useNavigation(conv, navigateTo, bumpReset)

  const handleFontSize = (action: 'increase' | 'decrease') =>
    setFontSize(prev => action === 'increase' ? Math.min(prev + 1, 25) : Math.max(prev - 1, 11))

  const screenStyle = (id: Screen) => getScreenStyle(id, screen, phase, transition, leavingScreen)

  return (
    <div
      className="w-screen h-screen select-none overflow-hidden relative"
      style={{
        backgroundImage: `url(${backgroundImg})`,
        backgroundSize: 'cover', backgroundPosition: 'center',
        backgroundColor: '#000', borderRadius: 0,
        display: 'flex', justifyContent: 'center',
      }}
    >
      <div className="relative h-full overflow-hidden" style={{ width: '100%', maxWidth: '450px' }}>
        {/* WERSJA - lewy górny róg */}
        <VersionDisplay />

        {showIntro && (
          <IntroScreen
            showLogo={showLogo}
            onDone={() => { /* E1 już widoczny pod spodem */ }}
            onUnmount={() => { setShowIntro(false); setE1Visible(true) }}
            onSplashReady={(unlock) => {
              // Splash doszedł do połowy i zatrzymał się — zapisz callback
              splashUnlockRef.current = unlock
              setSplashReady(true)
              // Jeśli auth już zakończona (szybka cicha auth) — od razu kontynuuj
              if (authState === 'authenticated') {
                unlock()
                splashUnlockRef.current = null
              }
            }}
          />
        )}

        {unauthorized.active && <UnauthorizedOverlay reason={unauthorized.reason} />}

        {splashReady && authState === 'unauthorized' && <InactivityLockScreen onUnlock={unlock} onUnlockWithToken={unlockWithToken} message={authReason ? `Brak autoryzacji: ${authReason}` : 'Aplikacja wymaga autoryzacji dedykowanym linkiem'} isUnauthorized />}
        {splashReady && authState === 'locked' && <InactivityLockScreen onUnlock={unlock} onUnlockWithToken={unlockWithToken} />}


        <div style={{ ...screenStyle(1), opacity: e1Visible ? 1 : 0, transition: e1Visible ? 'opacity 350ms ease-out' : 'none' }}>
          <Screen1
            positions={screen1Pos}
            micScale={micScale}
            conv={conv}
            onNavigateTo2={nav.goE1toE2}
            onUnauthorized={(reason) => setUnauthorized({ active: true, reason })}
            isActive={screen === 1}
            onResetSession={nav.resetSession}
            resetSignal={resetSignal}
            dbg={dbg}
          />
        </div>

        <div style={screenStyle(2)}>
          {conv.isIncident ? (
            <IncidentFormScreen
              positions={screen2Pos}
              conv={conv}
              onConfirm={nav.goE2toE3}
            />
          ) : (
            <Screen2
              positions={screen2Pos}
              viewportWidth={viewportWidth}
              fontSize={fontSize}
              onFontSizeChange={handleFontSize}
              conv={conv}
              onConfirm={nav.goE2toE3}
              onGoToE3={nav.goE2toE3}
              addE3Arrangement={nav.addE3Arrangement}
              arrangementList={nav.e3Data.arrangementList}
            />
          )}
        </div>

        <div style={screenStyle(3)}>
          {conv.isIncident ? (
            <IncidentSummaryScreen
              positions={screen3Pos}
              conv={conv}
              onGoToE2={nav.goE3toE2}
              onGoToE1={nav.resetSession}
            />
          ) : (
            <Screen3
              positions={screen3Pos}
              viewportWidth={viewportWidth}
              fontSize={fontSize}
              onFontSizeChange={handleFontSize}
              conv={conv}
              e3Data={nav.e3Data}
              setE3Body={nav.setE3Body}
              setE3Arrangements={nav.setE3Arrangements}
              addE3Arrangement={nav.addE3Arrangement}
              updateE3Arrangement={nav.updateE3Arrangement}
              removeE3Arrangement={nav.removeE3Arrangement}
              onGoToE2={nav.goE3toE2}
              onGoToE1={nav.goE3toE1}
            />
          )}
        </div>


      </div>

      {/* App Switcher Privacy Guard — czarna warstwa z logo przy minimalizacji */}
      {appHidden && (
        <div style={{
          position: 'fixed', inset: 0, zIndex: 99999,
          backgroundColor: '#000',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
        }}>
          <img src="/logo.png" alt="" style={{ width: '50vw', maxWidth: 400, opacity: 0.8 }} />
        </div>
      )}

    </div>
  )
}

// Komponent wyświetlający wersję w lewym górnym rogu
function VersionDisplay() {
  const [version, setVersion] = useState<string>('')
  const [visible, setVisible] = useState(false)

  useEffect(() => {
    fetch('/version.json')
      .then(r => r.json())
      .then(d => setVersion(d.version || ''))
      .catch(() => setVersion('?'))

    // Pokazuj wersję po 3s przytrzymania w lewym górnym rogu
    let timer: ReturnType<typeof setTimeout> | null = null
    const handleStart = () => { timer = setTimeout(() => setVisible(true), 3000) }
    const handleEnd = () => { if (timer) clearTimeout(timer); setVisible(false) }

    const el = document.getElementById('version-trigger')
    el?.addEventListener('touchstart', handleStart)
    el?.addEventListener('touchend', handleEnd)
    el?.addEventListener('mousedown', handleStart)
    el?.addEventListener('mouseup', handleEnd)

    return () => {
      el?.removeEventListener('touchstart', handleStart)
      el?.removeEventListener('touchend', handleEnd)
      el?.removeEventListener('mousedown', handleStart)
      el?.removeEventListener('mouseup', handleEnd)
    }
  }, [])

  return (
    <>
      {/* Trigger area - niewidoczny obszar w lewym górnym rogu */}
      <div id="version-trigger" style={{
        position: 'fixed', top: 0, left: 0, width: 60, height: 60, zIndex: 99998,
      }} />
      {/* Wersja - widoczna gdy przytrzymany trigger */}
      {visible && (
        <div style={{
          position: 'fixed', top: 10, left: 10, zIndex: 99999,
          background: 'rgba(0,0,0,0.8)', color: '#0f0', padding: '4px 8px',
          borderRadius: 4, fontSize: 12, fontFamily: 'monospace',
        }}>
          v: {version}
        </div>
      )}
    </>
  )
}

export default App